import { useState, useEffect, useRef } from "react";
import { langLabel } from "../lib/languages";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import RichTextEditor from "./RichTextEditor";
import EmailPreviewDialog from "./EmailPreviewDialog";
import { TranscriptEditor } from "./TranscriptEditor";
import { Mail, Sparkles, Save, Send, Paperclip, X, CheckCircle2, Settings, Mic, Users, User as UserIcon, BookOpen, Brain } from "lucide-react";
import { fetchModulePdf, getModuleContent } from "../lib/psychoeducationExport";
import { API } from "../App";
import { formatDateSafe } from "../lib/formatDate";

const TONE_LABELS = { chaleureux: "Chaleureux", professionnel: "Professionnel", formel: "Formel", empathique: "Empathique" };
const LENGTH_LABELS = { court: "Court", moyen: "Moyen", long: "Long" };

const fillVars = (text, patient, report, therapist) => {
  const first = patient?.first_name || (patient?.name || "").split(" ")[0] || "";
  const date = report?.session_date ? formatDateSafe(report.session_date, { day: "numeric", month: "long", year: "numeric" }) : "";
  return (text || "")
    .replace(/\{pr[ée]nom\}/gi, first).replace(/\{nom\}/gi, patient?.name || "")
    .replace(/\{date\}/gi, date).replace(/\{th[ée]rapeute\}/gi, therapist?.name || "");
};

const selectCls = "w-full px-3 py-2 text-sm border rounded-lg bg-background border-border";

const FollowupEmailPanel = ({ report, patient, user, onUpdated }) => {
  const [followup, setFollowup] = useState(report.followup || {});
  const [subject, setSubject] = useState(report.followup?.subject || "");
  const [body, setBody] = useState(report.followup?.body || "");
  const [options, setOptions] = useState({ models: [], tones: [], lengths: [], languages: [] });
  const [templates, setTemplates] = useState([]);
  const [smtp, setSmtp] = useState(null);
  const [gen, setGen] = useState({ model: "claude-sonnet-4-6", tone: "chaleureux", length: "moyen", language: patient?.language || "fr", instructions: "" });
  const [templateId, setTemplateId] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [copyMe, setCopyMe] = useState(false);
  const [files, setFiles] = useState([]);
  const [worksheets, setWorksheets] = useState([]);
  const [selectedWs, setSelectedWs] = useState({});
  useEffect(() => {
    axios.get(`${API}/session-reports/${report.report_id}/followup/worksheets`, { withCredentials: true })
      .then((r) => { const ws = r.data.worksheets || []; setWorksheets(ws); setSelectedWs(Object.fromEntries(ws.map((w) => [`${w.exercise_type}:${w.patient_id}`, true]))); })
      .catch(() => {});
  }, [report.report_id]);
  const wsKeys = Object.entries(selectedWs).filter(([, v]) => v).map(([k]) => k);
  const [modules, setModules] = useState([]);
  const [selectedMod, setSelectedMod] = useState({});
  useEffect(() => {
    axios.get(`${API}/session-reports/${report.report_id}/followup/modules`, { withCredentials: true })
      .then((r) => { const ms = (r.data.modules || []).filter((m) => getModuleContent(m.module_id, "fr")); setModules(ms); setSelectedMod(Object.fromEntries(ms.map((m) => [`${m.module_id}:${m.patient_id}`, false]))); })
      .catch(() => {});
  }, [report.report_id]);
  const [analyses, setAnalyses] = useState([]);
  const [selectedAn, setSelectedAn] = useState({});
  useEffect(() => {
    axios.get(`${API}/session-reports/${report.report_id}/followup/analyses`, { withCredentials: true })
      .then((r) => { const as = r.data.analyses || []; setAnalyses(as); setSelectedAn(Object.fromEntries(as.map((a) => [`${a.key}:${a.patient_id}`, false]))); })
      .catch(() => {});
  }, [report.report_id, report.analyses, report.analyses_by_patient]);
  const modulePdfFiles = async () => {
    const chosen = modules.filter((m) => selectedMod[`${m.module_id}:${m.patient_id}`] && (!individual || m.patient_id === targetId));
    return Promise.all(chosen.map(async (m) => {
      const blob = await fetchModulePdf(m.module_id, m.language || "fr", m.first_name || "");
      return new File([blob], `module-${m.module_id}-${m.language || "fr"}.pdf`, { type: "application/pdf" });
    }));
  };
  const [busy, setBusy] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [transcript, setTranscript] = useState(report.transcript || "");
  const [transcriptOpen, setTranscriptOpen] = useState(!report.transcript);
  const transcriptDirty = transcript.trim() !== (report.transcript || "").trim();
  const [participants, setParticipants] = useState([]);
  const [mode, setMode] = useState(report.followup?.mode || "commun");
  const [targetId, setTargetId] = useState("");
  const couple = participants.length > 1;
  const individual = couple && mode === "individuel";
  const target = individual ? participants.find((p) => p.user_id === targetId) : null;
  const message = individual ? (followup.variants || {})[targetId] || {} : followup;
  const firstName = (p) => p?.first_name || (p?.name || "").split(" ")[0] || p?.name || "";
  const anKeys = Object.entries(selectedAn).filter(([, v]) => v).map(([k]) => k).filter((k) => !individual || k.endsWith(`:${targetId}`));

  useEffect(() => {
    axios.get(`${API}/session-reports/${report.report_id}/followup/participants`, { withCredentials: true })
      .then((r) => {
        setParticipants(r.data.participants);
        if (r.data.participants.length > 1) {
          const m = r.data.mode || "commun";
          setMode(m);
          if (m === "individuel") switchTarget(r.data.participants[0].user_id, followup, m); else setTargetId(r.data.participants[0].user_id);
        }
      })
      .catch(() => {});
  }, [report.report_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTarget = (id, fu = followup, m = mode) => {
    setTargetId(id);
    const v = m === "individuel" ? (fu.variants || {})[id] || {} : fu;
    setSubject(v.subject || ""); setBody(v.body || "");
  };
  const switchMode = (m) => { setMode(m); switchTarget(targetId || participants[0]?.user_id, followup, m); };

  const saveTranscript = async () => {
    const res = await axios.put(`${API}/session-reports/${report.report_id}`, { transcript }, { withCredentials: true });
    onUpdated?.({ ...report, ...res.data, followup: followup });
    return res.data;
  };

  const handleSaveTranscript = async () => {
    setBusy("transcript");
    try { await saveTranscript(); toast.success("Transcription enregistrée dans le compte rendu"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };
  const fileRef = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/followup/options`, { withCredentials: true }),
      axios.get(`${API}/email-templates`, { withCredentials: true }),
      axios.get(`${API}/settings/smtp`, { withCredentials: true }),
    ]).then(([o, tp, s]) => { setOptions(o.data); setTemplates(tp.data); setSmtp(s.data); }).catch(() => {});
  }, []);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const tpl = templates.find((x) => x.id === id);
    if (!tpl) return;
    if (tpl.type === "texte") {
      setSubject(fillVars(tpl.subject, patient, report, user));
      setBody(fillVars(tpl.body, patient, report, user));
      toast.success("Modèle texte appliqué");
    } else {
      setGen((g) => ({ ...g, tone: tpl.tone, length: tpl.length, instructions: tpl.instructions || "" }));
      toast.success("Préréglage IA appliqué");
    }
  };

  const persist = (fu) => { setFollowup(fu); onUpdated?.({ ...report, transcript, followup: fu }); };

  const handleGenerate = async () => {
    setBusy("generate");
    try {
      if (transcriptDirty) await saveTranscript();
      const res = await axios.post(`${API}/session-reports/${report.report_id}/followup/generate`, { ...gen, mode, target_patient_id: individual ? targetId : null }, { withCredentials: true });
      const v = individual ? (res.data.variants || {})[targetId] || {} : res.data;
      setSubject(v.subject || ""); setBody(v.body || ""); persist(res.data);
      toast.success(individual ? `E-mail généré pour ${firstName(target)}` : couple ? "E-mail commun généré pour le couple" : "E-mail de suivi généré");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de génération"); }
    finally { setBusy(""); }
  };

  const handleSave = async () => {
    setBusy("save");
    try {
      const res = await axios.put(`${API}/session-reports/${report.report_id}/followup`, { subject, body, target_patient_id: individual ? targetId : null }, { withCredentials: true });
      persist(res.data); toast.success("Brouillon enregistré");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(""); }
  };

  const handleSend = async () => {
    setBusy("send");
    try {
      const fd = new FormData();
      fd.append("subject", subject); fd.append("body", body); fd.append("cc", cc); fd.append("target_patient_id", individual ? targetId : "");
      fd.append("bcc", [bcc, copyMe && smtp?.from_email].filter(Boolean).join(","));
      files.forEach((f) => fd.append("files", f));
      (await modulePdfFiles()).forEach((f) => fd.append("files", f));
      fd.append("worksheets", wsKeys.filter((k) => !individual || k.endsWith(`:${targetId}`)).join(","));
      fd.append("analyses", anKeys.join(","));
      const res = await axios.post(`${API}/session-reports/${report.report_id}/followup/send`, fd, { withCredentials: true });
      persist(res.data); setPreviewOpen(false); setFiles([]);
      if (anKeys.length) setSelectedAn((s) => Object.fromEntries(Object.keys(s).map((k) => [k, false])));
      toast.success(`E-mail envoyé à ${individual ? ((res.data.variants || {})[targetId] || {}).sent_to : res.data.sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'envoi"); }
    finally { setBusy(""); }
  };

  const previewAttachments = [
    ...files,
    ...wsKeys.filter((k) => !individual || k.endsWith(`:${targetId}`)).map((k) => ({ name: `fiche-${k.split(":")[0]}.pdf`, kind: "worksheet" })),
    ...modules.filter((m) => selectedMod[`${m.module_id}:${m.patient_id}`] && (!individual || m.patient_id === targetId))
      .map((m) => ({ name: `${getModuleContent(m.module_id, m.language || "fr")?.title || m.module_id} (module PDF, ${(m.language || "fr").toUpperCase()})`, kind: "module" })),
    ...analyses.filter((a) => anKeys.includes(`${a.key}:${a.patient_id}`)).map((a) => ({ name: `${a.label} (analyse PDF)`, kind: "analysis" })),
  ];
  const recipients = individual ? [target?.email].filter(Boolean) : couple ? participants.map((p) => p.email).filter(Boolean) : [patient?.email].filter(Boolean);
  const missingEmails = individual ? (target && !target.email ? [firstName(target)] : []) : couple ? participants.filter((p) => !p.email).map(firstName) : [];
  const canSend = recipients.length > 0 && missingEmails.length === 0 && !!smtp?.configured && body.replace(/<[^>]+>/g, "").trim().length > 0;
  const status = message.status;

  return (
    <div className="mt-4 border rounded-xl p-4 space-y-4 bg-muted/20" data-testid={`followup-panel-${report.report_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="font-medium flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /> E-mail de suivi</h4>
        {status === "envoye" ? (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid="followup-status-sent">
            <CheckCircle2 className="w-3.5 h-3.5" /> Envoyé le {formatDateSafe(message.sent_at)}{message.sent_to ? ` à ${message.sent_to}` : ""}
          </span>
        ) : status === "genere" ? (
          <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" data-testid="followup-status-generated">Généré</span>
        ) : null}
      </div>

      {smtp && !smtp.configured && (
        <p className="text-xs text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300 rounded-lg p-2 flex items-center gap-2" data-testid="smtp-missing-warning">
          <Settings className="w-3.5 h-3.5" /> Configurez votre SMTP dans <Link to="/profile#messaging" className="underline">Mon profil → Messagerie &amp; IA</Link> pour pouvoir envoyer.
        </p>
      )}
      {!couple && !patient?.email && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="patient-email-missing">Ce patient n'a pas d'adresse e-mail : renseignez-la dans sa fiche.</p>}

      {couple && (
        <div className="rounded-lg border border-rose-200 bg-rose-50/50 dark:bg-rose-950/20 p-3 space-y-2" data-testid="followup-couple-box">
          <p className="text-sm font-medium flex items-center gap-2"><Users className="w-4 h-4 text-rose-600" /> Thérapie de couple — {participants.map(firstName).join(" & ")}</p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border overflow-hidden bg-background" data-testid="followup-mode-toggle">
              <button type="button" onClick={() => switchMode("commun")} className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${mode === "commun" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="followup-mode-commun"><Users className="w-3.5 h-3.5" /> E-mail commun (aux deux)</button>
              <button type="button" onClick={() => switchMode("individuel")} className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${mode === "individuel" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="followup-mode-individuel"><UserIcon className="w-3.5 h-3.5" /> Un e-mail par partenaire</button>
            </div>
            {individual && (
              <div className="flex rounded-lg border overflow-hidden bg-background" data-testid="followup-target-toggle">
                {participants.map((p) => { const v = (followup.variants || {})[p.user_id] || {}; return (
                  <button key={p.user_id} type="button" onClick={() => switchTarget(p.user_id)} className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${targetId === p.user_id ? "bg-rose-600 text-white" : "hover:bg-muted"}`} data-testid={`followup-target-${p.user_id}`}>
                    {firstName(p)}{v.status === "envoye" ? <CheckCircle2 className="w-3 h-3" /> : v.body ? <span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> : null}
                  </button>); })}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground" data-testid="followup-recipients">
            {individual ? `Destinataire : ${firstName(target)} ${target?.email ? `<${target.email}>` : "(aucune adresse e-mail)"} — l'e-mail est personnalisé pour ce partenaire ; générez-en un pour chacun.` : `Destinataires : ${participants.map((p) => `${firstName(p)} ${p.email ? `<${p.email}>` : "(aucune adresse)"}`).join(" · ")} — un seul e-mail adressé au couple.`}
          </p>
          {missingEmails.length > 0 && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="couple-email-missing">Adresse e-mail manquante pour : {missingEmails.join(", ")}. Renseignez-la dans la fiche{!individual ? " ou passez en mode « un e-mail par partenaire »" : ""}.</p>}
        </div>
      )}

      <div className="rounded-lg border bg-background/60 p-3 space-y-2" data-testid="followup-transcript-section">
        <button type="button" onClick={() => setTranscriptOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 text-left" data-testid="followup-transcript-toggle">
          <span className="text-sm font-medium flex items-center gap-2"><Mic className="w-4 h-4 text-primary" /> Transcription de la séance (source de l'e-mail)</span>
          <span className="text-xs text-muted-foreground">
            {transcript.trim() ? `${transcript.trim().length.toLocaleString("fr-FR")} caractères${transcriptDirty ? " · non enregistrée" : ""}` : "Aucune — collez celle de votre enregistreur (Plaud…)"} {transcriptOpen ? "▲" : "▼"}
          </span>
        </button>
        {transcriptOpen && (
          <>
            <TranscriptEditor value={transcript} onChange={setTranscript} disabled={busy !== ""} rows={8} testId="followup-transcript" />
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs text-muted-foreground">L'IA rédige l'e-mail à partir de cette transcription (sinon à partir des champs du CR). Elle est conservée dans le compte rendu et alimente le dossier patient.</p>
              <Button variant="outline" size="sm" onClick={handleSaveTranscript} disabled={busy !== "" || !transcriptDirty} className="gap-1.5" data-testid="followup-save-transcript-btn">
                <Save className="w-3.5 h-3.5" /> {busy === "transcript" ? "Enregistrement..." : "Enregistrer la transcription"}
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="col-span-2 md:col-span-1"><Label className="text-xs">Modèle IA</Label>
          <select className={selectCls} value={gen.model} onChange={(e) => setGen({ ...gen, model: e.target.value })} data-testid="followup-model-select">
            {options.models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          {options.forced_model && <p className="text-[11px] text-emerald-700 mt-1" data-testid="followup-vertex-hint">HDS : génération via Vertex AI ({options.vertex?.location}, UE) — {options.forced_model}</p>}</div>
        <div><Label className="text-xs">Ton</Label>
          <select className={selectCls} value={gen.tone} onChange={(e) => setGen({ ...gen, tone: e.target.value })} data-testid="followup-tone-select">
            {options.tones.map((x) => <option key={x} value={x}>{TONE_LABELS[x] || x}</option>)}
          </select></div>
        <div><Label className="text-xs">Longueur</Label>
          <select className={selectCls} value={gen.length} onChange={(e) => setGen({ ...gen, length: e.target.value })} data-testid="followup-length-select">
            {options.lengths.map((x) => <option key={x} value={x}>{LENGTH_LABELS[x] || x}</option>)}
          </select></div>
        <div><Label className="text-xs">Langue</Label>
          <select className={selectCls} value={gen.language} onChange={(e) => setGen({ ...gen, language: e.target.value })} data-testid="followup-language-select">
            {options.languages.map((x) => <option key={x} value={x}>{langLabel(x)}</option>)}
          </select></div>
        <div><Label className="text-xs">Modèle d'e-mail</Label>
          <select className={selectCls} value={templateId} onChange={(e) => applyTemplate(e.target.value)} data-testid="followup-template-select">
            <option value="">— Aucun —</option>
            {templates.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}{tp.type === "texte" ? " · texte" : ""}</option>)}
          </select></div>
      </div>
      <Textarea value={gen.instructions} onChange={(e) => setGen({ ...gen, instructions: e.target.value })} placeholder="Instructions spécifiques pour l'IA (optionnel)" rows={2} data-testid="followup-instructions" />
      <Button onClick={handleGenerate} disabled={busy !== ""} className="gap-2" data-testid="followup-generate-btn">
        <Sparkles className="w-4 h-4" /> {busy === "generate" ? "Génération..." : message.body ? "Régénérer l'e-mail" : "Générer l'e-mail avec l'IA"}{individual ? ` pour ${firstName(target)}` : couple ? " (commun)" : ""}
      </Button>

      <div className="space-y-2">
        <Label className="text-xs">Objet</Label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Objet de l'e-mail" data-testid="followup-subject-input" />
        <Label className="text-xs">Corps</Label>
        <RichTextEditor value={body} onChange={setBody} placeholder="Corps de l'e-mail..." />
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Cc (séparés par des virgules)" data-testid="followup-cc-input" />
        <Input value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="Cci" data-testid="followup-bcc-input" />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={copyMe} onCheckedChange={(v) => setCopyMe(!!v)} data-testid="followup-copy-me" /> Me mettre en copie</label>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])} data-testid="followup-file-input" />
        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="gap-1.5" data-testid="followup-attach-btn"><Paperclip className="w-3.5 h-3.5" /> Pièce jointe</Button>
        {files.map((f, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded">{f.name}
            <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button></span>
        ))}
      </div>

      {worksheets.length > 0 && (
        <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5" data-testid="followup-worksheets">
          <p className="text-xs font-medium flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Fiches papier des exercices assignés (PDF joints automatiquement)</p>
          <div className="flex flex-wrap gap-2">
            {worksheets.map((w) => { const k = `${w.exercise_type}:${w.patient_id}`; return (
              <label key={k} className="inline-flex items-center gap-1.5 text-xs bg-background border rounded-md px-2 py-1 cursor-pointer" data-testid={`followup-ws-${w.exercise_type}`}>
                <Checkbox checked={!!selectedWs[k]} onCheckedChange={(v) => setSelectedWs({ ...selectedWs, [k]: !!v })} /> {w.title || w.exercise_type}{worksheets.some((o) => o.patient_id !== w.patient_id) ? ` · ${w.first_name}` : ""}
              </label>); })}
          </div>
        </div>
      )}
      {modules.length > 0 && (
        <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5" data-testid="followup-modules">
          <p className="text-xs font-medium flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Modules de psychoéducation assignés (PDF complet : chapitres, illustrations, quiz et corrigé)</p>
          <div className="flex flex-wrap gap-2">
            {modules.map((m) => { const k = `${m.module_id}:${m.patient_id}`; const c = getModuleContent(m.module_id, m.language || "fr"); return (
              <label key={k} className="inline-flex items-center gap-1.5 text-xs bg-background border rounded-md px-2 py-1 cursor-pointer" data-testid={`followup-module-${m.module_id}`}>
                <Checkbox checked={!!selectedMod[k]} onCheckedChange={(v) => setSelectedMod({ ...selectedMod, [k]: !!v })} /> {c?.title || m.module_id}{modules.some((o) => o.patient_id !== m.patient_id) ? ` · ${m.first_name}` : ""} <span className="text-muted-foreground uppercase">{m.language}</span>
              </label>); })}
          </div>
        </div>
      )}
      {analyses.length > 0 && (
        <div className="rounded-lg border bg-muted/30 p-3 space-y-1.5" data-testid="followup-analyses">
          <p className="text-xs font-medium flex items-center gap-1.5"><Brain className="w-3.5 h-3.5" /> Analyses de la séance (PDF version patient)</p>
          <div className="flex flex-wrap gap-2">
            {analyses.filter((a) => !individual || a.patient_id === targetId).map((a) => { const k = `${a.key}:${a.patient_id}`; return (
              <label key={k} className="inline-flex items-center gap-1.5 text-xs bg-background border rounded-md px-2 py-1 cursor-pointer" title={a.title} data-testid={`followup-analysis-${a.key}`}>
                <Checkbox checked={!!selectedAn[k]} onCheckedChange={(v) => setSelectedAn({ ...selectedAn, [k]: !!v })} /> {a.label}{analyses.some((o) => o.patient_id !== a.patient_id) ? ` · ${a.first_name}` : ""}
                {a.sent_to_patient_at && <span className="text-emerald-600" title={`Déjà envoyée le ${formatDateSafe(a.sent_to_patient_at)}`}><CheckCircle2 className="w-3 h-3" /></span>}
              </label>); })}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 flex-wrap">
        <Button variant="outline" onClick={handleSave} disabled={busy !== ""} className="gap-2" data-testid="followup-save-btn"><Save className="w-4 h-4" /> Enregistrer le brouillon</Button>
        <Button onClick={() => setPreviewOpen(true)} disabled={!canSend || busy !== ""} className="gap-2" data-testid="followup-preview-send-btn"><Send className="w-4 h-4" /> Prévisualiser et envoyer</Button>
      </div>

      <EmailPreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} smtp={smtp} to={recipients.join(", ")} cc={cc}
        bcc={[bcc, copyMe && smtp?.from_email].filter(Boolean).join(", ")} subject={subject} body={body} attachments={previewAttachments}
        onConfirm={handleSend} sending={busy === "send"} lang={gen.language} />
    </div>
  );
};

export default FollowupEmailPanel;
